import { Facebook, Instagram, Linkedin, Twitter, Youtube, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// =====================================================================
// SocialPlatformPicker — 6-icon row select for footer.socialLinks
// platform enum. Custom (vs SegmentedControl) because labels for the
// platforms would be too wide at 360px. Active option lifts on
// bg-card + shadow-sm, same active-state pattern as SegmentedControl.
//
// lucide doesn't ship a TikTok icon; we inline a minimal SVG glyph
// (paths only, currentColor) for visual consistency with the others.
// =====================================================================

type Platform = 'twitter' | 'instagram' | 'facebook' | 'linkedin' | 'tiktok' | 'youtube';

function TiktokIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

const OPTIONS: Array<{ value: Platform; label: string; Icon: LucideIcon | typeof TiktokIcon }> = [
  { value: 'twitter', label: 'X / Twitter', Icon: Twitter },
  { value: 'instagram', label: 'Instagram', Icon: Instagram },
  { value: 'facebook', label: 'Facebook', Icon: Facebook },
  { value: 'linkedin', label: 'LinkedIn', Icon: Linkedin },
  { value: 'tiktok', label: 'TikTok', Icon: TiktokIcon },
  { value: 'youtube', label: 'YouTube', Icon: Youtube },
];

interface SocialPlatformPickerProps {
  value: Platform;
  onChange: (next: Platform) => void;
}

export function SocialPlatformPicker({ value, onChange }: SocialPlatformPickerProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-foreground">Platform</span>
      <div
        role="radiogroup"
        aria-label="Platform"
        className={cn(
          'inline-flex p-0.5 gap-0.5',
          'rounded-[var(--radius-sm)]',
          'bg-surface-sunken border border-border-subtle',
          'self-start',
        )}
      >
        {OPTIONS.map(({ value: v, label, Icon }) => {
          const active = v === value;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={label}
              title={label}
              onClick={() => {
                if (!active) onChange(v);
              }}
              className={cn(
                'h-8 w-8 inline-flex items-center justify-center',
                'rounded-[calc(var(--radius-sm)-2px)]',
                'transition-colors duration-[var(--duration-fast)]',
                'focus-visible:outline-2 focus-visible:outline-brand-500',
                'focus-visible:outline-offset-1',
                active
                  ? 'bg-card text-brand-700 shadow-sm'
                  : 'text-text-secondary hover:text-foreground',
              )}
            >
              <Icon className="h-4 w-4" aria-hidden />
            </button>
          );
        })}
      </div>
    </div>
  );
}
